'use client';

import { useMemo } from 'react';

import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/navigation';
import { getPresaleEndMs } from '@/lib/utils/getPresaleEndMs';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';
import { T_Pool } from '@/ts/types/T_Pool';

interface I_SwapTokenButtonProps {
  pools: T_Pool[];
}

export function SwapTokenButton({ pools }: I_SwapTokenButtonProps) {
  const t = useTranslations();

  // Check if any pool has active presale (presaleEndDate hasn't ended)
  const hasActivePresale = useMemo(() => {
    if (pools.length === 0) return false;

    return pools.some(pool => {
      if (!pool.presaleEndDate) return false;
      const presaleEndMs = getPresaleEndMs(pool.presaleEndDate);
      if (presaleEndMs === null) return false;
      // Presale is active if end date is in the future
      return Date.now() < presaleEndMs;
    });
  }, [pools]);

  // Get first pool for navigation
  const firstPool = useMemo(() => {
    return pools.length > 0 ? pools[0] : null;
  }, [pools]);

  // Determine link href for pools
  const poolsLinkHref = useMemo(() => {
    if (pools.length === 0) return null;

    // Check if there's at least one pool with active presale
    if (hasActivePresale) {
      return C_Routes[E_Routes.POOLS_PRESALE].link;
    }
    // Otherwise, navigate to pools page with first pool's slug
    if (firstPool?.slug) {
      return `/pools/${firstPool.slug}`;
    }

    return null;
  }, [pools, hasActivePresale, firstPool]);

  if (!poolsLinkHref) {
    return (
      <Button className="w-full h-10" disabled>
        {t('transactions.filter.swapTokens')}
      </Button>
    );
  }

  return (
    <Button className="w-full h-10" asChild>
      <Link href={poolsLinkHref}>{t('transactions.filter.swapTokens')}</Link>
    </Button>
  );
}
